import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

import SectionContent from '../../components/common/sections/SectionContent';
import PageHeader from '../../components/common/sections/PageHeader';
import LegalCard from '../../components/common/cards/LegalCard';
import { LEGAL_CONTENT } from '../../data';
import THEME from '../../constants/theme';
import { SECTION_SPACING, ELEMENT_SPACING } from '../../constants';
import { ContactLink } from '../../components';
import { pdfFiles } from '../../utils';
import ImageTextOverlay from '../../components/common/media/ImageTextOverlay';
// Import the image
import mqcapitalImage21 from '../../assets/images/legal/mqcapital-image-21.jpg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const LegalPage = () => {
  // Handle card click
  const handleCardClick = doc => {
    if (doc.IS_PDF && doc.PDF_ID) {
      // Open PDF in a new tab
      window.open(pdfFiles[doc.PDF_ID], '_blank');
    } else if (doc.PATH) {
      // Use direct window.location for navigation from legal pages
      // This helps solve the navigation issue
      window.location.href = doc.PATH;
    }
  };

  return (
    <>
      <SEO
        title={seoData.legal.title}
        content={seoData.legal.content}
        keywords={seoData.legal.keywords}
        canonical={seoData.legal.canonical}
        ogImage={seoData.legal.ogImage}
      />
      {/* Main "Legal" section */}}
      <PageHeader
        id='hero'
        title={LEGAL_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <SectionContent
        id='legalInformation'
        initialSpacing={false}
        text={[
          {
            text: LEGAL_CONTENT.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          // Render the first paragraph normally
          {
            text: LEGAL_CONTENT.INTRO[0],
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Render the second paragraph as ImageTextOverlay with background image */}
      <Box
        sx={{
          width: 'auto',
          mx: { xs: -3, sm: -4, md: -5, lg: -6 }, // Negative margin to extend full-width
          my: 4 // Add vertical margin
        }}
      >
        <ImageTextOverlay
          imageSrc={mqcapitalImage21}
          text={LEGAL_CONTENT.INTRO[1]}
          height={{ xs: '350px', sm: '300px', md: '275px', lg: '275px' }}
          boxSx={{
            top: '1%',
            width: {
              xs: 'calc(100% - 16px)',
              sm: 'calc(100% - 48px)',
              md: '60%'
            },
            left: { md: 0 },
            transform: { md: 'none' },
            pl: { md: 3 }
          }}
          textSx={{ fontSize: '1.3rem', lineHeight: 1.6 }}
          overlayColor='rgba(0,0,0,0.3)'
        />
      </Box>
      {/* Legal Documents Grid */}
      <Box sx={{ mb: SECTION_SPACING, mt: SECTION_SPACING }}>
        <Grid container spacing={3}>
          {LEGAL_CONTENT.DOCUMENTS.map((doc, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <LegalCard
                title={doc.TITLE}
                description={doc.DESCRIPTION}
                isPdf={doc.IS_PDF}
                onClick={() => handleCardClick(doc)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* Footer note with separator line - This is NOT the site footer */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {LEGAL_CONTENT.FOOTER_NOTE}
        </Typography>
        <Box mt={ELEMENT_SPACING}>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6
            }}
          >
            Talk to an Expert –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your meeting
            </ContactLink>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LegalPage;
